import React, { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Map as MapIcon, MessageSquarePlus } from 'lucide-react';

import { FlexColumn } from 'src/components/flex';
import LeadSidebarExpandableItem from 'src/components/lead-sidebar/LeadSidebarExpandableItem';
import SimilarPropertiesModal, {
    buildPropertyPitch,
    findSimilarProperties,
    formatDistance,
    getDefaultFilters,
    getLeadSignals,
} from 'src/components/similar-properties-modal';
import { useSimilarPropertiesContext } from 'src/components/similar-properties-modal/SimilarPropertiesContext';
import { formatPropertySummary } from 'src/components/property-details-card';
import { useProperties, useProperty } from 'src/services/api';
import type { ILead } from 'src/services/api';
import type { IProperty } from 'src/services/api/properties/types';
import { EFeatureFlag, useIsFeatureEnabled } from 'src/utils/feature-flags';

import styles from 'src/components/lead-sidebar/LeadSidebarXSellTab.module.css';

/**
 * Entry point to the map of comparable properties around the one this lead is
 * assigned to, for when the assigned unit is not a fit and an agent needs an
 * alternative to offer in the conversation.
 *
 * Offered even when the lead has no assigned property, where it becomes a map of the
 * team's vacant inventory — that is precisely the case where an agent has nothing to
 * suggest yet and most needs somewhere to start.
 */
const PREVIEWED_MATCH_COUNT = 3;

type SimilarPropertiesSectionProps = {
    property: IProperty | null;
    lead?: ILead | null;
    /** Appends text to the composer draft. Absent outside the messages page. */
    onInsertMessage?: (text: string) => void;
};

/**
 * Gated by the `x_sell` Flagsmith flag. Kept as a wrapper so that with
 * the flag off the section costs nothing — no property list fetch, no matching.
 */
function SimilarPropertiesSection(props: SimilarPropertiesSectionProps) {
    const isEnabled = useIsFeatureEnabled(EFeatureFlag.X_SELL);

    if (!isEnabled) {
        return null;
    }

    return <SimilarProperties {...props} />;
}

function SimilarProperties({ property, lead, onInsertMessage }: SimilarPropertiesSectionProps) {
    /*
     * The conversation page mounts a SimilarPropertiesProvider high enough for the
     * sidebar chip, the composer nudge, and this section to all share one modal
     * instance. The leads list page never mounts one, so this section falls back to
     * owning the modal itself there — see the local state below.
     */
    const pageContext = useSimilarPropertiesContext();
    const [localOpenAtId, setLocalOpenAtId] = useState<number | null>(null);
    const [isLocalMapOpen, setLocalMapOpen] = useState(false);
    const { data: properties = [] } = useProperties();

    const signals = useMemo(() => getLeadSignals(lead ?? null, property), [lead, property]);

    const matches = useMemo(() => {
        return findSimilarProperties(property, properties, getDefaultFilters(property), signals).matches;
    }, [property, properties, signals]);

    const isDistanceRanked = property?.latitude != null && property.longitude != null;
    const countNoun = matches.length === 1 ? 'property' : 'properties';

    let summary: string;
    if (property == null) {
        summary = `${matches.length} vacant ${countNoun} across the team.`;
    } else if (isDistanceRanked) {
        summary = matches.length > 1 ?
            `${matches.length} comparable ${countNoun} vacant within 5 mi, ranked by fit.` :
            `${matches.length} comparable ${countNoun} vacant within 5 mi.`;
    } else {
        summary = `${property.name} has no coordinates yet, so these are not ranked by distance.`;
    }

    const openMap = (activeId: number | null) => {
        if (pageContext) {
            pageContext.openSimilarProperties(activeId);
            return;
        }
        setLocalOpenAtId(activeId);
        setLocalMapOpen(true);
    };

    return (
        <>
            <LeadSidebarExpandableItem
                title={property == null ? 'Available Properties' : 'Similar Properties'}
                defaultExpanded
            >
                <FlexColumn rowGap={1} pb={1}>
                    <Typography variant="body2" color="text.secondary">
                        {summary}
                    </Typography>

                    {/* the nearest few inline, so the section answers the question
                        instead of only advertising that an answer exists elsewhere */}
                    {matches.slice(0, PREVIEWED_MATCH_COUNT).map(({ property: match, distanceMiles }) => (
                        <div key={match.id} className={styles.similarMatchRow}>
                            <button
                                type="button"
                                className={styles.similarMatch}
                                onClick={() => openMap(match.id)}
                                data-testid={`similar-match-${match.id}`}
                            >
                                <Typography
                                    variant="body2"
                                    component="span"
                                    className={styles.similarMatchName}
                                >
                                    {match.name}
                                </Typography>
                                {/* summary left, distance right-aligned on the summary's
                                    last line — the one carrying the rent */}
                                <span className={styles.similarMatchMetaRow}>
                                    <Typography variant="caption" component="span" color="text.secondary">
                                        {formatPropertySummary(match)}
                                    </Typography>
                                    {distanceMiles != null && (
                                        <Typography
                                            variant="caption"
                                            component="span"
                                            className={styles.similarMatchDistance}
                                        >
                                            {formatDistance(distanceMiles)}
                                        </Typography>
                                    )}
                                </span>
                            </button>
                            {onInsertMessage && (
                                <Tooltip title="Add to message">
                                    <IconButton
                                        size="small"
                                        className={styles.similarMatchInsert}
                                        onClick={() => onInsertMessage(buildPropertyPitch(match))}
                                        aria-label={`Add ${match.name} to the message`}
                                        data-testid={`similar-match-insert-${match.id}`}
                                    >
                                        <MessageSquarePlus size={16} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    ))}

                    <Button
                        variant="text"
                        size="small"
                        startIcon={<MapIcon size={15} />}
                        onClick={() => openMap(null)}
                        className={styles.similarOpenMap}
                        data-testid="open-similar-properties-map-button"
                    >
                        {matches.length > PREVIEWED_MATCH_COUNT ?
                            `Open map · all ${matches.length}` :
                            'Open map'}
                    </Button>
                </FlexColumn>
            </LeadSidebarExpandableItem>
            {!pageContext && isLocalMapOpen && (
                <SimilarPropertiesModal
                    anchor={property}
                    signals={signals}
                    initialActiveId={localOpenAtId}
                    onInsertMessage={onInsertMessage}
                    onClose={() => setLocalMapOpen(false)}
                />
            )}
        </>
    );
}

type Props = {
    leadPropertyId: number | null;
    lead?: ILead | null;
    /** Appends text to the composer draft. Absent outside the messages page. */
    onInsertMessage?: (text: string) => void;
};

export default function LeadSidebarXSellTab({ leadPropertyId, lead, onInsertMessage }: Props) {
    const { data: property } = useProperty(leadPropertyId, {
        include_ancestors: true,
        include_facts: true,
    });

    return (
        <SimilarPropertiesSection property={property ?? null} lead={lead} onInsertMessage={onInsertMessage} />
    );
}
